import { useCreation, useRequest } from 'ahooks'
import { Cascader } from 'antd'
import { isNil, last } from 'lodash'

import { NewsCatalogueEntity } from '@eastcoal/api/content/interface'
import { queryNewsCatalogueTreeApi } from '@eastcoal/api/content/newsCatalogueOperation/queryNewsCatalogueTree'

import { DataEmpty } from '@eastcoal/common/components/DataEmpty'
import { DataError } from '@eastcoal/common/components/DataError'
import { toArray, findTreePaths } from '@eastcoal/common/utils/array'
import { pick } from '@eastcoal/common/utils/object'

import { connect, mapReadPretty } from '@eastcoal/formily/exports/react'

import { NewsCatalogueCascaderProps } from './interface'

const fieldNames = {
  value: 'code',
  label: 'name',
  children: 'children'
} as const

const pickValue = (item?: NewsCatalogueEntity) => {
  return item
    ? pick(item, ['id', 'parentId', 'name', 'builtin', 'code'])
    : undefined
}

const Component = <M extends boolean = false>({
  parentId,
  parentCode,
  value,
  onChange,
  /* -------------------------------------------------------------------------- */
  /*                               Cascader Props                               */
  /* -------------------------------------------------------------------------- */
  multiple,
  allowClear,
  disabled
}: NewsCatalogueCascaderProps<M>) => {
  const { data, error, loading, refreshAsync } = useRequest(
    () =>
      queryNewsCatalogueTreeApi({
        parentId,
        parentCode
      }),
    {
      manual: false,
      refreshDeps: [parentId]
    }
  )

  const selectedKeys = useCreation(() => {
    if (isNil(data)) {
      return undefined
    }

    if (isNil(value)) {
      return undefined
    }

    const nodesArr = toArray(value).map(item =>
      findTreePaths(
        data,
        node => node.id === item.id || node.code === item.code
      )
    )

    const paths = nodesArr.map(nodes => nodes.map(node => `${node.code}`))

    const keys = multiple ? paths : paths[0]

    return keys as any
  }, [data, multiple, value])

  const handleChange = (
    _: unknown,
    options: NewsCatalogueEntity[] | NewsCatalogueEntity[][]
  ) => {
    const values = multiple
      ? (options as NewsCatalogueEntity[][]).map(item => pickValue(last(item)))
      : pickValue(last(options as NewsCatalogueEntity[]))
    onChange?.(values as any)
  }

  return error ? (
    <DataError error={error} reload={refreshAsync} />
  ) : (
    <Cascader
      changeOnSelect
      allowClear={allowClear}
      disabled={disabled}
      fieldNames={fieldNames}
      loading={loading}
      multiple={multiple}
      notFoundContent={<DataEmpty loading={loading} reload={refreshAsync} />}
      options={data}
      showCheckedStrategy={Cascader.SHOW_CHILD}
      value={selectedKeys}
      onChange={handleChange}
    />
  )
}

export const NewsCatalogueCascader = connect(
  Component,
  mapReadPretty(Component, {
    disabled: true
  })
)
